// ── V126: 발음 표시 ──────────────────────────────────────────────────────────
// 한국어 화면: 중국어 밑에 병음(성조 포함)   예) 酱油 → jiàng yóu
// 중국어 화면: 한국어 밑에 중국인이 병음처럼 읽는 발음(V128)   예) 간장 → gan zang
//
// 병음은 pinyin-pro(MIT)로 만든다. 사전이 커서(약 150KB) 처음 필요할 때만 따로 불러온다.
// 불러오기 전·실패하면 발음 줄만 안 보일 뿐 나머지 화면은 그대로다.
import { useEffect, useState } from 'react';

// ── 병음 ────────────────────────────────────────────────────────────────────
let pinyinFn = null;
let loading = null;
const listeners = new Set();

function loadPinyin() {
  if (pinyinFn) return Promise.resolve();
  if (!loading) {
    loading = import('pinyin-pro')
      .then(m => {
        pinyinFn = m.pinyin;
        const fs = [...listeners];
        listeners.clear();
        fs.forEach(f => { try { f(); } catch (e) { /* 화면이 이미 사라짐 */ } });
      })
      .catch(e => {
        console.debug('[pron] 병음 사전 불러오기 실패:', e?.message);
        loading = null;   // 다음에 다시 시도
      });
  }
  return loading;
}

const HAN = /[㐀-鿿]/;
const pyCache = new Map();

/** 중국어 → 병음 (성조 포함). 사전이 아직 없으면 '' (불러오기는 시작한다) */
export function pinyinOf(zh) {
  const s = String(zh == null ? '' : zh).trim();
  if (!s || !HAN.test(s)) return '';
  if (!pinyinFn) { loadPinyin(); return ''; }
  if (pyCache.has(s)) return pyCache.get(s);
  let out = '';
  try {
    out = String(pinyinFn(s, { toneType: 'symbol', nonZh: 'consecutive' }) || '')
      .replace(/\s+/g, ' ').trim();
  } catch (e) { out = ''; }
  pyCache.set(s, out);
  return out;
}

/** 병음 사전이 준비됐는가 (인쇄·엑셀은 준비된 뒤에 만든다) */
export function isPinyinReady() { return !!pinyinFn; }

/** 병음 사전을 불러온다. 실패해도 resolve (그때는 발음 없이 진행) */
export function ensurePinyin() {
  return (loadPinyin() || Promise.resolve()).then(() => !!pinyinFn, () => false);
}

/**
 * V127: 인쇄·엑셀용 발음 한 줄. 한국어 화면이면 중국어의 병음, 중국어 화면이면 한국어 발음(병음식, V128).
 * @returns {string} 없으면 ''
 */
export function pronText(ko, zh, lang) {
  return lang === 'zh' ? romanizeKo(ko) : (zh ? pinyinOf(zh) : '');
}

/** 병음 사전이 준비되면 다시 그리게 한다 */
export function usePinyinReady(enabled) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!enabled || pinyinFn) return undefined;
    const f = () => tick(n => n + 1);
    listeners.add(f);
    loadPinyin();
    return () => { listeners.delete(f); };
  }, [enabled]);
  return !!pinyinFn;
}

// ── 한국어 발음 — 중국인이 병음처럼 읽는 표기 (V128) ───────────────────────────
// 예전(V126)엔 한국 로마자 표기법(gan jang, tteok bo kki)이었다. 중국 직원은 알파벳을 병음 규칙으로 읽으므로
// eo·eu·ch·r 같은 글자가 엉뚱하게 읽힌다. 그래서 '병음 글자값'으로 한국어 소리에 가장 가깝게 적는다.
//   ㅈ→z ㅊ→c ㅅ→s (ㅣ·ㅑ·ㅕ 같은 i 소리 앞에선 j q x), ㄹ→l (r 아님), ㄲㄸㅃㅆㅉ → g d b s z (된소리는 병음에 없음)
//   ㅡ→e (z c s 뒤에선 i: 스→si), ㅓ→e, ㅐ→ai, ㅔ→ei, ㅑ→ia/ya, ㅕ·ㅖ→ie/ye, ㅛ→io/yo, ㅠ→iu/you, ㅜ→u/wu, ㅣ→i/yi
//   받침: ㄴ n, ㅇ ng, ㅁ m, ㄹ l, ㄱ k, ㄷ t, ㅂ p (대표음). 받침 뒤 ㅇ이면 다음 글자로 넘긴다(볶이 → bo gi)
// 성조는 없다 (한국어엔 성조가 없으므로).
//   간장 → gan zang · 떡볶이 → dek bo gi · 김치찌개 → gim qi ji gai · 설탕 → sel tang · 스시 → si xi
const PY_I = ['g', 'g', 'n', 'd', 'd', 'l', 'm', 'b', 'b', 's', 's', '', 'z', 'z', 'c', 'k', 't', 'p', 'h'];
const PALATAL = { 9: 'x', 10: 'x', 12: 'j', 13: 'j', 14: 'q' };      // ㅅㅆ→x, ㅈㅉ→j, ㅊ→q (i 소리 앞)
const SIBILANT = new Set([9, 10, 12, 13, 14]);                          // ㅅㅆㅈㅉㅊ — 뒤의 ㅡ는 i (zi ci si)
// 모음: [자음 뒤, 첫소리 없을 때(ㅇ)], i 소리로 시작하는가
// (ㅐ는 중국 쪽 음역 관례대로 ai — 대한 大韩 dà, 해운대 海云台 hǎi / ㅚ·ㅞ는 ue — 된장 duen zang)
const PY_V = [
  ['a', 'a', 0], ['ai', 'ai', 0], ['ia', 'ya', 1], ['ie', 'ye', 1], ['e', 'e', 0], ['ei', 'ei', 0], ['ie', 'ye', 1], ['ie', 'ye', 1],
  ['o', 'o', 0], ['ua', 'wa', 0], ['uai', 'wai', 0], ['ue', 'wei', 0], ['io', 'yo', 1], ['u', 'wu', 0], ['uo', 'wo', 0], ['ue', 'wei', 0],
  ['ui', 'wei', 0], ['iu', 'you', 1], ['e', 'e', 0], ['i', 'yi', 1], ['i', 'yi', 1],
];
// 받침 대표음 (JONG 순서: 없음 ㄱ ㄲ ㄳ ㄴ ㄵ ㄶ ㄷ ㄹ ㄺ ㄻ ㄼ ㄽ ㄾ ㄿ ㅀ ㅁ ㅂ ㅄ ㅅ ㅆ ㅇ ㅈ ㅊ ㅋ ㅌ ㅍ ㅎ)
const PY_F = ['', 'k', 'k', 'k', 'n', 'n', 'n', 't', 'l', 'k', 'm', 'l', 'l', 'l', 'p', 'l', 'm', 'p', 'p', 't', 't', 'ng', 't', 't', 'k', 't', 'p', 't'];
// 받침 + ㅇ → [남는 받침, 다음 글자 첫소리(초성 번호, -1 = 소리 없음)]
const LIAISON = {
  1: ['', 0], 2: ['', 1], 3: ['k', 9], 4: ['', 2], 5: ['n', 12], 6: ['', 2], 7: ['', 3], 8: ['', 5],
  9: ['l', 0], 10: ['l', 6], 11: ['l', 7], 12: ['l', 9], 13: ['l', 16], 14: ['l', 17], 15: ['', 5],
  16: ['', 6], 17: ['', 7], 18: ['p', 9], 19: ['', 9], 20: ['', 10], 22: ['', 12], 23: ['', 14],
  24: ['', 15], 25: ['', 16], 26: ['', 17], 27: ['', -1],
};

function pySyllable(ci, vi, fin) {
  const [withC, alone, iLike] = PY_V[vi];
  let v;
  let ini;
  if (ci === 11 || ci === -1) {          // 첫소리 없음
    ini = '';
    v = alone;
  } else {
    ini = (iLike && PALATAL[ci]) || PY_I[ci];
    v = withC;
    if (vi === 18 && SIBILANT.has(ci)) v = 'i';                         // 스 → si, 즈 → zi, 츠 → ci
  }
  return ini + v + fin;
}

const roCache = new Map();

/** 한국어 → 중국인이 병음처럼 읽는 발음. 글자마다 띄우고, 낱말 사이는 ' · '. 한글이 없으면 '' */
export function romanizeKo(ko) {
  const s = String(ko == null ? '' : ko).trim();
  if (!s || !/[가-힣]/.test(s)) return '';
  if (roCache.has(s)) return roCache.get(s);
  const words = s.split(/\s+/).map(word => {
    const toks = [];
    let buf = '';
    for (const ch of word) {
      const code = ch.charCodeAt(0) - 0xAC00;
      if (code < 0 || code >= 11172) { buf += ch; continue; }
      if (buf) { toks.push({ raw: buf }); buf = ''; }
      toks.push({ ci: Math.floor(code / 588), vi: Math.floor((code % 588) / 28), fi: code % 28 });
    }
    if (buf) toks.push({ raw: buf });

    const parts = [];
    let carry = null;                     // 앞 글자에서 넘어온 첫소리(초성 번호)
    toks.forEach((tk, i) => {
      if (tk.raw) { parts.push(tk.raw); carry = null; return; }
      const next = toks[i + 1];
      const ci = tk.ci === 11 && carry !== null ? carry : tk.ci;
      carry = null;
      let fin = PY_F[tk.fi];
      if (tk.fi && next && !next.raw && next.ci === 11 && LIAISON[tk.fi]) {
        const [stay, move] = LIAISON[tk.fi];
        fin = stay;
        carry = move;
      }
      parts.push(pySyllable(ci, tk.vi, fin));
    });
    return parts.join(' ');
  });
  const out = words.filter(Boolean).join(' · ');
  roCache.set(s, out);
  return out;
}
